import {
  ApplicantStatus,
  ExecutionModes,
  VerificationStatus,
  VerificationNames,
  ApplicantSettings
} from "shared/types";
import { ReactNode } from "react";
import { Applicant, Application } from "types/application";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import { DefinedQueryObserverResult } from "@tanstack/react-query";
import CreditReportSlice from "..";
import { ManualReportView } from "views/applications-id-report/components/ReportsFallback/ManualReportView";
import { SkippedReportView } from "views/applications-id-report/components/ReportsFallback/SkippedReportView";
import { ProgressReportView } from "views/applications-id-report/components/ReportsFallback/ProgressReportView";
import { PendingReportView } from "views/applications-id-report/components/ReportsFallback/PendingReportView";
import { UnavailableReportView } from "views/applications-id-report/components/ReportsFallback/UnavailableReportView";
import { SkippedReasonView } from "views/applications-id-report/components/ReportsFallback/SkippedReasonView";

const STATUS_VIEWS: Partial<Record<VerificationStatus, ReactNode>> = {
  [VerificationStatus.Pending]: <PendingReportView />,
  [VerificationStatus.Canceled]: (
    <PendingReportView message="Report was canceled" />
  ),
  [VerificationStatus.Started]: <ProgressReportView />,
  [VerificationStatus.NoDataAvailable]: <UnavailableReportView />
};

const getPastDueAccountRate = (
  application: Application,
  role: Applicant["role"]
) =>
  application.verification_template?.screening_criteria
    ? application.verification_template.screening_criteria[`${role}_settings`]
        ?.past_due_account?.past_due_account_rate
    : "";

export const CreditReportByStatus = ({
  verificationCreditReport,
  isFetched,
  applicant,
  applicationMeta,
  screeningCriteria,
  selectedVerificationId,
  activeReportId,
  openRerunModal,
  setOpenRerunModal,
  isIdentityVerificationOverrideRequired,
  isSIN
}: {
  verificationCreditReport?: VerificationReport<CreditReport>;
  isFetched?: boolean;
  applicant: Applicant;
  applicationMeta: DefinedQueryObserverResult<Application | undefined, unknown>;
  screeningCriteria?: ApplicantSettings;
  selectedVerificationId: number;
  activeReportId?: number;
  openRerunModal: boolean;
  setOpenRerunModal: (val: boolean) => void;
  isIdentityVerificationOverrideRequired: boolean;
  isSIN?: boolean;
}) => {
  if (verificationCreditReport?.skips_for_canadian_resident)
    return (
      <SkippedReasonView
        title="Credit report will be skipped"
        message="Applicant has a Canadian address. The US credit report will be skipped — use the Canadian credit report instead."
      />
    );

  if (
    verificationCreditReport?.status === VerificationStatus.Skipped &&
    verificationCreditReport.skip_reason
  )
    return (
      <SkippedReasonView
        title="Credit report was skipped"
        message={verificationCreditReport.skip_reason}
      />
    );

  if (
    verificationCreditReport?.status === VerificationStatus.Pending &&
    verificationCreditReport.settings?.execution_mode === ExecutionModes.Manual
  )
    return (
      <ManualReportView
        refetch={applicationMeta.refetch}
        applicantStatus={
          applicationMeta.data!.applicants.find(
            (appl) => appl.id === applicant.id
          )?.application_status
        }
        applicationStatus={applicationMeta.data!.status}
        applicantId={applicant.id}
        verificationId={verificationCreditReport.id}
        applicationId={applicationMeta.data!.id}
        verificationName={VerificationNames.CreditReport}
        creditReportStatus={verificationCreditReport.status}
      />
    );

  if (
    (verificationCreditReport?.status === VerificationStatus.Pending ||
      verificationCreditReport?.status === VerificationStatus.Skipped) &&
    verificationCreditReport.settings?.execution_mode ===
      ExecutionModes.Conditionally
  )
    return (
      <SkippedReportView
        refetch={applicationMeta.refetch}
        applicantId={applicant.id}
        verificationId={verificationCreditReport.id}
        applicationId={applicationMeta.data!.id}
        verificationStatus={verificationCreditReport.status}
      />
    );

  if (
    verificationCreditReport?.status === VerificationStatus.Pending &&
    (verificationCreditReport.is_rerun ||
      applicant.application_status === ApplicantStatus.Submitted)
  )
    return (
      <ProgressReportView
        message={
          verificationCreditReport.is_rerun
            ? "Report re-run in progress. Please wait"
            : undefined
        }
      />
    );

  if (isIdentityVerificationOverrideRequired)
    return (
      <PendingReportView message="Please review issues with applicant's identity verification report. This report will not run until identity is verified" />
    );

  const statusView =
    verificationCreditReport && STATUS_VIEWS[verificationCreditReport.status];
  if (statusView) return statusView;

  return (
    <CreditReportSlice
      creditReport={verificationCreditReport}
      isFetched={isFetched}
      applicant={applicant}
      screeningCriteria={screeningCriteria}
      applicationStatus={applicationMeta.data!.status}
      verificationId={selectedVerificationId}
      activeReportId={activeReportId}
      openRerunModal={openRerunModal}
      setOpenRerunModal={setOpenRerunModal}
      isSIN={isSIN}
      pastDueAccountType={getPastDueAccountRate(
        applicationMeta.data!,
        applicant.role
      )}
    />
  );
};
